/**
 * @orgus/tokens: typed access to the design tokens. Browser safe.
 *
 * Components style with CSS variables (tokens.css) or the Tailwind utilities mapped to them;
 * this module is for code that needs a token by name: SVG fills, canvas charts, the design
 * catalogue, contrast checks.
 */
import { DENSITIES, THEMES, TOKENS } from './tokens.generated.ts';

export { DENSITIES, LEGACY, PRIMITIVES, THEMES, TOKENS } from './tokens.generated.ts';
export { composite, contrastRatio, CONTRAST_TEXT, CONTRAST_UI, luminance, parseHex, toHex, withAlpha } from './color.ts';
export type { Rgba } from './color.ts';

export type TokenName = keyof typeof TOKENS;
export type ThemeName = (typeof THEMES)[number]['name'];
export type DensityName = (typeof DENSITIES)[number]['name'];

export const THEME_NAMES: readonly ThemeName[] = THEMES.map((t) => t.name);
export const DENSITY_NAMES: readonly DensityName[] = DENSITIES.map((d) => d.name);
export const DEFAULT_THEME: ThemeName = THEMES[0].name;
export const DEFAULT_DENSITY: DensityName = DENSITIES[0].name;

/** `var(--status-working)` for `color.status.working`. Follows the active theme and density. */
export function cssVar(name: TokenName): string {
  return `var(${TOKENS[name].css})`;
}

/** The literal value of a token in one theme and density (for canvas and contrast maths). */
export function tokenValue(name: TokenName, theme: ThemeName = DEFAULT_THEME, density: DensityName = DEFAULT_DENSITY): string {
  const v: unknown = TOKENS[name].value;
  if (typeof v === 'string') return v;
  const byMode = v as Record<string, string>;
  return byMode[theme] ?? byMode[density] ?? Object.values(byMode)[0] ?? '';
}

/**
 * A duration token in milliseconds, for code that animates through WAAPI or schedules a timer
 * (`tokenMs('duration.tick')` is 1000). Throws for a token that is not a duration.
 * The value is the token's own; under reduced motion check `prefers-reduced-motion` yourself
 * (the CSS variables already read 0 ms there, except duration.fade-reduced).
 */
export function tokenMs(name: TokenName): number {
  const t = TOKENS[name];
  if (t.type !== 'duration') throw new Error(`${name} is a ${t.type} token, not a duration`);
  const v = tokenValue(name);
  const n = Number.parseFloat(v);
  return v.endsWith('ms') ? n : v.endsWith('s') ? n * 1000 : n;
}

function tokenNumber(name: TokenName): number {
  return Number.parseFloat(tokenValue(name));
}

/**
 * The live layer's timers and budgets (component.json `live.*`) as plain numbers. Timers are
 * not animation and do not change under reduced motion. See docs/design-system/language/motion.md.
 */
export const LIVE = {
  staleMs: tokenMs('live.stale-after'),
  recapAwayMs: tokenMs('live.recap-away'),
  lineMinDwellMs: tokenMs('live.line-min-dwell'),
  wall: {
    cutMinMs: tokenMs('live.wall.cut-min'),
    holdReleaseMs: tokenMs('live.wall.hold-release'),
    shiftIntervalMs: tokenMs('live.wall.shift-interval'),
    shiftPx: tokenNumber('live.wall.shift'),
  },
  budget: {
    animations: tokenNumber('live.budget.animations'),
    animationsCalm: tokenNumber('live.budget.animations-calm'),
    flowDots: tokenNumber('live.budget.flow-dots'),
    flowDotsWall: tokenNumber('live.budget.flow-dots-wall'),
    flowDotsPerEdge: tokenNumber('live.budget.flow-dots-per-edge'),
    spinnersPerGroup: tokenNumber('live.budget.spinners-per-group'),
  },
} as const;

export function isThemeName(v: unknown): v is ThemeName {
  return typeof v === 'string' && (THEME_NAMES as readonly string[]).includes(v);
}

export function isDensityName(v: unknown): v is DensityName {
  return typeof v === 'string' && (DENSITY_NAMES as readonly string[]).includes(v);
}
